import axios from 'axios';

const API_DEBUG_LOGS = process.env.REACT_APP_API_DEBUG === 'true';

const trimTrailingSlash = (value = '') => String(value || '').trim().replace(/\/+$/, '');

const ensureApiSuffix = (value = '') => {
 const normalized = trimTrailingSlash(value);
 if (!normalized) return '';
 return /\/api$/i.test(normalized) ? normalized : `${normalized}/api`;
};

const isLocalHostName = (hostname = '') => {
 const normalized = String(hostname || '').trim().toLowerCase();
 return normalized === 'localhost' || normalized === '127.0.0.1';
};

const normalizeApiBaseUrl = (rawUrl = '') => {
 const input = String(rawUrl || '').trim();
 if (!input) return '';

 if (input.startsWith('/')) {
 return ensureApiSuffix(input);
 }

 const withProtocol = /^https?:\/\//i.test(input) ? input : `http://${input}`;

 try {
 const parsed = new URL(withProtocol);
 const sanitizedOrigin = `${parsed.protocol}//${parsed.host}`;
 const pathName = parsed.pathname || '';
 const fullPath = `${sanitizedOrigin}${pathName}`;
 return ensureApiSuffix(fullPath);
 } catch (error) {
 return ensureApiSuffix(withProtocol);
 }
};

const buildHostBasedApiUrl = (hostname, protocol = 'http:', port = '5000') => {
 const safeHost = String(hostname || '').trim();
 if (!safeHost) return '';
 const portSuffix = String(port || '').trim() ? `:${String(port).trim()}` : '';
 return `${protocol}//${safeHost}${portSuffix}/api`;
};

const resolveApiUrl = () => {
 const envBackendUrl = normalizeApiBaseUrl(process.env.REACT_APP_BACKEND_URL);
 const envApiUrl = normalizeApiBaseUrl(process.env.REACT_APP_API_URL);
 const envPreferredUrl = envBackendUrl || envApiUrl;

 if (typeof window !== 'undefined') {
 const currentHost = window.location.hostname;
 const currentProtocol = window.location.protocol || 'http:';

 if (envPreferredUrl) {
 try {
 const parsed = new URL(envPreferredUrl);
 const envUsesLocalHost = isLocalHostName(parsed.hostname);
 const appUsesLocalHost = isLocalHostName(currentHost);

 // Trường hợp build để localhost nhưng truy cập từ máy khác (điện thoại/LAN)
 if (envUsesLocalHost && !appUsesLocalHost) {
 const fallbackPort = parsed.port || '5000';
 return buildHostBasedApiUrl(currentHost, currentProtocol, fallbackPort);
 }

 return envPreferredUrl;
 } catch (error) {
 // Ignore parse error and continue fallback
 }
 }

 // Production/reverse-proxy: ưu tiên same-origin để tránh CORS/mixed-content
 if (!isLocalHostName(currentHost)) {
 return `${trimTrailingSlash(window.location.origin)}/api`;
 }
 }

 if (envPreferredUrl) return envPreferredUrl;

 return process.env.NODE_ENV === 'production'
 ? 'https://quanly.newatlantic.vn/api'
 : 'http://localhost:5000/api';
};

const API_URL = resolveApiUrl();

// Tạo một instance của axios với cấu hình cơ bản
const API = axios.create({
 baseURL: API_URL,
 // Don't set default Content-Type here, let axios set it based on the request
 // Thêm timeout để tránh request bị treo
 timeout: 10000, // 10 seconds (long requests sẽ override riêng)
 withCredentials: true,
});

let unauthorizedRedirectScheduled = false;

const getRequestPath = (url) => {
 if (!url || typeof url !== 'string') return '';
 if (url.startsWith('/')) return url;

 try {
 return new URL(url).pathname;
 } catch (error) {
 return url;
 }
};

const QUIET_BACKGROUND_PATHS = [
 '/ai/knowledge/status',
 '/notifications/unread-count',
 '/system-status',
 '/user-activity/stats',
 '/user-activity/update',
 '/page-visibility/bulk-check'
];

// Một số endpoint POST thuần truy vấn (không ghi dữ liệu nghiệp vụ),
// có thể retry/fallback baseURL an toàn khi lỗi mạng production.
const SAFE_WRITE_NETWORK_FALLBACK_PATHS = [
 '/ai/hr-chat'
];

const isQuietBackgroundPath = (url) => {
 const path = getRequestPath(url);
 return QUIET_BACKGROUND_PATHS.some((prefix) => path.startsWith(prefix));
};

const buildFullRequestUrl = (baseURL, url) => {
 const base = String(baseURL || '').trim();
 const target = String(url || '').trim();

 if (!target) return base;
 if (/^https?:\/\//i.test(target)) return target;

 if (!base) return target;

 const normalizedBase = trimTrailingSlash(base);
 const normalizedTarget = target.startsWith('/') ? target : `/${target}`;
 return `${normalizedBase}${normalizedTarget}`;
};

const shouldAttemptNetworkFallback = (error) => {
 if (!error || !error.config || error.config.__networkFallbackTried) return false;

 const networkLikeError = error.code === 'ERR_NETWORK'
 || (error.code === 'ECONNABORTED' && !error.response)
 || (!error.response && /network\s*error/i.test(String(error.message || '')));

 if (!networkLikeError) return false;

 const method = String(error.config?.method || 'get').toLowerCase();
 const requestPath = getRequestPath(error.config.url);
 if (!requestPath) return false;

 // Endpoint đọc: fallback bình thường
 if (['get', 'head', 'options'].includes(method)) {
 return requestPath.startsWith('/ai/knowledge') || requestPath.startsWith('/ai/');
 }

 // Một số endpoint POST dạng truy vấn AI cho phép fallback một lần
 if (method === 'post') {
 return SAFE_WRITE_NETWORK_FALLBACK_PATHS.some((pathPrefix) => requestPath.startsWith(pathPrefix));
 }

 return false;
};

const getFallbackApiBaseUrls = (currentBaseUrl = '') => {
 const candidates = [];

 if (typeof window !== 'undefined') {
 const origin = trimTrailingSlash(window.location.origin);
 if (origin) candidates.push(`${origin}/api`);

 const host = window.location.hostname;
 const protocol = window.location.protocol || 'http:';
 const localHost = isLocalHostName(host);

 if (host) {
 // Nếu localhost thì chỉ thử http để tránh lỗi SSL self-signed (https://localhost:5000)
 if (localHost) {
 candidates.push(buildHostBasedApiUrl(host, 'http:', '5000'));
 } else {
 // Ưu tiên protocol hiện tại
 candidates.push(buildHostBasedApiUrl(host, protocol, '5000'));
 candidates.push(buildHostBasedApiUrl(host, 'http:', '5000'));
 candidates.push(buildHostBasedApiUrl(host, 'https:', '5000'));
 }
 }
 }

 const envBackendUrl = normalizeApiBaseUrl(process.env.REACT_APP_BACKEND_URL);
 const envApiUrl = normalizeApiBaseUrl(process.env.REACT_APP_API_URL);
 const envFallbackApiUrl = normalizeApiBaseUrl(process.env.REACT_APP_FALLBACK_API_URL);
 const envSecondaryBackendUrl = normalizeApiBaseUrl(process.env.REACT_APP_BACKEND_URL_SECONDARY);

 if (envBackendUrl) candidates.push(envBackendUrl);
 if (envApiUrl) candidates.push(envApiUrl);
 if (envFallbackApiUrl) candidates.push(envFallbackApiUrl);
 if (envSecondaryBackendUrl) candidates.push(envSecondaryBackendUrl);

 const normalizedCurrent = normalizeApiBaseUrl(currentBaseUrl);
 return [...new Set(candidates.map((item) => normalizeApiBaseUrl(item)).filter(Boolean))]
 .filter((item) => item !== normalizedCurrent);
};

const isLoginRequest = (url) => {
 const path = getRequestPath(url);
 return path.includes('/auth/login') || path.includes('auth/login');
};

const hasLocalSession = () => {
 try {
 const storedUser = localStorage.getItem('user');
 if (!storedUser) return false;

 const parsedUser = JSON.parse(storedUser);
 const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');
 return Boolean(authToken && parsedUser && (parsedUser.id || parsedUser._id || parsedUser.username));
 } catch (error) {
 localStorage.removeItem('user');
 return false;
 }
};

const getStoredAuthToken = () => {
 try {
 return localStorage.getItem('authToken') || localStorage.getItem('token') || null;
 } catch (error) {
 return null;
 }
};

const isPublicAuthRequest = (url) => {
 const path = getRequestPath(url);
 return (
 path.includes('/auth/login') ||
 path.includes('/auth/register') ||
 path.includes('/auth/forgot-password') ||
 path.includes('/auth/reset-password')
 );
};

const isOnAuthPage = () => {
 if (typeof window === 'undefined') return false;
 return window.location.pathname === '/login' || window.location.pathname === '/register';
};

const shouldSkipErrorLogging = (error) => {
 if (error?.config?.skipErrorInterceptorLog) return true;
 if (error?.code === 'ERR_CANCELED') return true;

 const responseData = error?.response?.data || {};
 const errorCode = responseData.errorCode || responseData.code;
 if (errorCode === 'JOB_CANCELLED') return true;

 const isTransientBackendError = (
 error?.code === 'ERR_NETWORK'
 || error?.code === 'ECONNABORTED'
 || [502, 503, 504].includes(error?.response?.status)
 );

 if (isTransientBackendError && isQuietBackgroundPath(error?.config?.url)) {
 return true;
 }

 return false;
};

const clearLocalSession = () => {
 localStorage.removeItem('authToken');
 localStorage.removeItem('token');
 localStorage.removeItem('user');
 window.dispatchEvent(new Event('authChange'));
};

API.interceptors.request.use(
 (config) => {
 config.headers = config.headers || {};

 if (API_DEBUG_LOGS) {
 console.log('[API INTERCEPTOR] ===== REQUEST INTERCEPTOR =====');
 console.log('[API INTERCEPTOR] Method:', config.method?.toUpperCase());
 console.log('[API INTERCEPTOR] URL:', config.url);
 console.log('[API INTERCEPTOR] Base URL:', config.baseURL);
 console.log('[API INTERCEPTOR] Full URL:', buildFullRequestUrl(config.baseURL, config.url));
 console.log('[API INTERCEPTOR] Headers before:', config.headers);
 }

 const authToken = getStoredAuthToken();
 if (authToken && !isPublicAuthRequest(config.url)) {
 config.headers['x-auth-token'] = authToken;
 if (!config.headers.Authorization) {
 config.headers.Authorization = `Bearer ${authToken}`;
 }
 }

 // Cookie-based auth: token is sent automatically via httpOnly cookie.
 if (config.data instanceof FormData) {
 if (API_DEBUG_LOGS) {
 console.log('[API INTERCEPTOR] FormData detected, letting browser set Content-Type header');
 }
 delete config.headers['Content-Type'];
 } else if (!config.headers['Content-Type']) {
 config.headers['Content-Type'] = 'application/json';
 }

 if (API_DEBUG_LOGS) {
 console.log('[API INTERCEPTOR] Final headers:', config.headers);
 console.log('[API INTERCEPTOR] Final config:', config);
 console.log('[API INTERCEPTOR] ===== REQUEST INTERCEPTOR END =====');
 }

 return config;
 },
 (error) => {
 console.error('[API INTERCEPTOR] Request interceptor error:', error);
 return Promise.reject(error);
 }
);

// Đã loại bỏ SessionExpiredModal vì không còn cần thiết

// Cấu hình xử lý token hết hạn và các lỗi khác
API.interceptors.response.use(
 (response) => {
 if (API_DEBUG_LOGS) {
 console.log('[API INTERCEPTOR] ===== RESPONSE SUCCESS =====');
 console.log('[API INTERCEPTOR] Status:', response.status);
 console.log('[API INTERCEPTOR] URL:', response.config?.url);
 console.log('[API INTERCEPTOR] Data type:', typeof response.data);
 console.log('[API INTERCEPTOR] ===== RESPONSE SUCCESS END =====');
 }
 return response;
 },
 async (error) => {
 // Log chi tiết lỗi để debug
 if (!shouldSkipErrorLogging(error)) {
 console.error('[API INTERCEPTOR] ===== RESPONSE ERROR =====');
 console.error('[API INTERCEPTOR] Error details:', {
 url: error.config?.url,
 baseURL: error.config?.baseURL,
 fullUrl: buildFullRequestUrl(error.config?.baseURL, error.config?.url),
 method: error.config?.method,
 status: error.response?.status,
 statusText: error.response?.statusText,
 data: error.response?.data,
 message: error.message,
 code: error.code,
 request: error.request ? 'Present' : 'Missing'
 });
 if (API_DEBUG_LOGS) {
 console.error('[API INTERCEPTOR] Full error object:', error);
 }
 console.error('[API INTERCEPTOR] ===== RESPONSE ERROR END =====');
 }
 
 if (shouldAttemptNetworkFallback(error)) {
 // Retry 1: giữ nguyên URL nhưng bỏ withCredentials để tránh kẹt CORS credential policy
 if (error.config?.withCredentials === true && !error.config?.__withoutCredentialsRetryTried) {
 try {
 if (API_DEBUG_LOGS) {
 console.warn('[API INTERCEPTOR] Retrying once without credentials for CORS compatibility');
 }

 return await API.request({
 ...error.config,
 withCredentials: false,
 __networkFallbackTried: true,
 __withoutCredentialsRetryTried: true,
 skipErrorInterceptorLog: true
 });
 } catch (credentialFallbackError) {
 if (API_DEBUG_LOGS) {
 console.warn('[API INTERCEPTOR] Retry without credentials failed:', credentialFallbackError?.message);
 }
 }
 }

 const fallbackBaseUrls = getFallbackApiBaseUrls(error.config?.baseURL);

 for (const fallbackBaseUrl of fallbackBaseUrls) {
 try {
 if (API_DEBUG_LOGS) {
 console.warn('[API INTERCEPTOR] Retrying with fallback baseURL:', fallbackBaseUrl);
 }

 return await API.request({
 ...error.config,
 baseURL: fallbackBaseUrl,
 __networkFallbackTried: true,
 skipErrorInterceptorLog: true
 });
 } catch (fallbackError) {
 if (API_DEBUG_LOGS) {
 console.warn('[API INTERCEPTOR] Fallback baseURL failed:', fallbackBaseUrl, fallbackError?.message);
 }
 }
 }
 }
 
 if (error.response && error.response.status === 401) {
 const responseData = error.response.data || {};
 const requestUrl = error.config?.url || '';
 const userHadSession = hasLocalSession();

 // Không can thiệp lỗi 401 của chính API login
 if (isLoginRequest(requestUrl)) {
 return Promise.reject(error);
 }

 // Nếu không có session local thì chỉ reject, không redirect liên tục trên trang login
 if (!userHadSession) {
 console.log('401 received without local session. Skipping forced redirect.');
 return Promise.reject(error);
 }
 
 // Kiểm tra nếu là session bị terminate từ thiết bị khác
 if (responseData.sessionExpired) {
 console.log('Session terminated from another device. Redirecting to login...');
 
 // Show notification về việc bị đăng xuất từ thiết bị khác
 if (window.showToast) {
 window.showToast('warning', 'Phiên đăng nhập đã bị chấm dứt từ thiết bị khác. Vui lòng đăng nhập lại.');
 } else {
 alert('Phiên đăng nhập đã bị chấm dứt từ thiết bị khác. Vui lòng đăng nhập lại.');
 }
 } else {
 console.log('Session expired. Redirecting to login...');
 }
 
 // Tự động đăng xuất và chuyển hướng
 clearLocalSession();

 // Tránh set nhiều redirect timers khi có nhiều request 401 cùng lúc
 if (!isOnAuthPage() && !unauthorizedRedirectScheduled) {
 unauthorizedRedirectScheduled = true;

 // Delay một chút để toast hiển thị trước khi redirect
 setTimeout(() => {
 window.location.href = '/login';
 }, 1000);
 }
 }
 else if (error.response && error.response.status === 403) {
 const responseData = error.response.data;

 // Check if account is deactivated or inactive
 if (responseData.accountStatus === 'deleted' || responseData.accountStatus === 'inactive') {
 console.log('Account deactivated. Logging out...');
 
 // Show alert about account deactivation
 alert(responseData.message || 'Tài khoản của bạn đã bị vô hiệu hóa. Vui lòng liên hệ quản trị viên.');
 
 // Force logout
 localStorage.removeItem('authToken');
 localStorage.removeItem('token');
 localStorage.removeItem('user');
 window.location.href = '/login';
 return Promise.reject(error);
 }

 // Check if this is a profile incomplete error
 if (responseData.error === 'Profile incomplete' && responseData.redirectTo) {
 console.log('Profile incomplete. Redirecting to complete-profile...');
 console.log('Profile completion status:', responseData.profileCompletion);

 // Show toast notification
 if (window.showToast) {
 window.showToast('warning', 'Vui lòng hoàn thành thông tin cá nhân để tiếp tục sử dụng hệ thống');
 }

 // Redirect to complete profile page
 window.location.href = responseData.redirectTo;
 return Promise.reject(error);
 }

 console.error('Access forbidden:', responseData);
 }
 else if (error.response && error.response.status === 500) {
 console.error('Server error:', error.response.data);
 // Có thể thêm xử lý đặc biệt cho lỗi server
 }
 
 return Promise.reject(error);
 }
);

// Component rỗng để duy trì tương thích với mã hiện tại
export const APIWithSession = () => {
 return null;
};

const logActivity = (action, actionType, details, relatedEntity) => {
 return post('/activity-logs', { action, actionType, details, relatedEntity });
};

const getActivityLogs = ({ actionType, userId, startDate, endDate, page = 1, limit = 10 }) => {
 const queryParams = [];
 if (actionType) queryParams.push(`actionType=${actionType}`);
 if (userId) queryParams.push(`userId=${userId}`);
 if (startDate) queryParams.push(`startDate=${startDate}`);
 if (endDate) queryParams.push(`endDate=${endDate}`);
 queryParams.push(`page=${page}`);
 queryParams.push(`limit=${limit}`);
 const queryString = queryParams.length > 0 ? '?' + queryParams.join('&') : '';
 return get(`/activity-logs${queryString}`);
};

// Hàm retry cho các request
const retryRequest = async (apiCall, maxRetries = 3, delay = 1000) => {
 let lastError;
 const totalAttempts = Math.max(1, Number(maxRetries) || 1);
 const nonRetryableStatusCodes = new Set([400, 401, 403, 404, 409, 422, 429]);
 
 for (let attempt = 0; attempt < totalAttempts; attempt++) {
 try {
 return await apiCall();
 } catch (error) {
 lastError = error;
 
 const status = error.response?.status;

 // Mất mạng/kết nối backend: không retry vòng lặp để tránh spam request/log
 if (error?.code === 'ERR_NETWORK') {
 throw error;
 }

 // Không retry các lỗi xác định chắc chắn request sẽ không thành công nếu lặp lại
 if (status && nonRetryableStatusCodes.has(status)) {
 throw error;
 }
 
 // Nếu không phải là lần thử cuối cùng, chờ một khoảng thời gian trước khi thử lại
 if (attempt < totalAttempts - 1) {
 console.log(`Attempt ${attempt + 1} failed, retrying in ${delay}ms...`);
 await new Promise(resolve => setTimeout(resolve, delay));
 // Tăng thời gian chờ cho lần thử tiếp theo (exponential backoff)
 delay *= 2;
 }
 }
 }
 
 // Nếu đã thử hết số lần mà vẫn thất bại, throw lỗi cuối cùng
 throw lastError;
};

// Các hàm API với retry
const get = (url, config = {}) => {
 const maxRetries = config.retry ?? 1;
 const retryDelay = config.retryDelay ?? 1000;
 
 // Xóa các tham số retry khỏi config trước khi gửi đến axios
 const { retry, retryDelay: delay, ...axiosConfig } = config;
 
 return retryRequest(() => API.get(url, axiosConfig), maxRetries, retryDelay);
};

const post = (url, data, config = {}) => {
 // POST mặc định không nên retry nhiều lần để tránh gửi trùng dữ liệu
 const maxRetries = config.retry ?? 1;
 const retryDelay = config.retryDelay ?? 1000;
 
 const { retry, retryDelay: delay, ...axiosConfig } = config;
 
 return retryRequest(() => API.post(url, data, axiosConfig), maxRetries, retryDelay);
};

const put = (url, data, config = {}) => {
 const maxRetries = config.retry ?? 1;
 const retryDelay = config.retryDelay ?? 1000;
 
 const { retry, retryDelay: delay, ...axiosConfig } = config;
 
 return retryRequest(() => API.put(url, data, axiosConfig), maxRetries, retryDelay);
};

// Thêm method PATCH với cơ chế retry tương tự
const patch = (url, data, config = {}) => {
 const maxRetries = config.retry ?? 1;
 const retryDelay = config.retryDelay ?? 1000;
 
 const { retry, retryDelay: delay, ...axiosConfig } = config;
 
 return retryRequest(() => API.patch(url, data, axiosConfig), maxRetries, retryDelay);
};

const del = (url, config = {}) => {
 const maxRetries = config.retry ?? 1;
 const retryDelay = config.retryDelay ?? 1000;
 
 const { retry, retryDelay: delay, ...axiosConfig } = config;
 
 // Log the delete request for debugging
 console.log('Delete request:', { url, config: axiosConfig });
 
 return retryRequest(() => API.delete(url, axiosConfig), maxRetries, retryDelay);
};

export default {
 get,
 post,
 put,
 patch,
 delete: del,
 logActivity,
 getActivityLogs,
 // Giữ lại các phương thức gốc để tương thích ngược
 originalGet: API.get,
 originalPost: API.post,
 originalPut: API.put,
 originalPatch: API.patch,
 originalDelete: API.delete,
};
